'use client'

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import Image from 'next/image'
import { getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { cn } from '@/lib/utils'
import { absoluteUrl } from '@/lib/site-seo'
import { openFancybox } from '@/lib/open-fancybox'

interface Photo {
  id: number
  image: string
  imageSort: number | null
  title?: string | null
  titleSub?: string | null
}

interface PhotoGalleryProps {
  photos: Photo[]
  newsTitle: string
  newsId: number
  categorySlug: string
  newsSlug: string
  content?: string | null
  newsDescription?: string | null
}

const INITIAL_VISIBLE = 1
const LOAD_STEP = 1

function getVisibleCountFromHash(total: number): number | null {
  if (typeof window === 'undefined') return null
  const match = window.location.hash.match(/^#photo(\d+)$/)
  if (!match) return null
  const target = parseInt(match[1], 10)
  if (Number.isNaN(target) || target < 1 || target > total) return null
  return target
}

export function extractContentParagraphs(html: string): string[] {
  if (!html) return []
  const blocks = html.match(/<p[^>]*>[\s\S]*?<\/p>/gi) || []
  return blocks
    .map((block) =>
      block
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean)
}

function isAutoPhotoTitle(title: string, newsTitle: string): boolean {
  const escaped = newsTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`^${escaped}\\s*-\\s*Foto\\s*\\d+$`, 'i').test(title.trim())
}

function photoDescription(photo: Photo): string {
  return String(photo.titleSub || (photo as { title_sub?: string }).title_sub || '').trim()
}

export function getPhotoCaption(
  photo: Photo,
  index: number,
  contentParagraphs: string[],
  newsTitle: string,
  newsDescription?: string | null,
) {
  const rawTitle = photo.title?.trim() || ''
  const title = rawTitle && !isAutoPhotoTitle(rawTitle, newsTitle) ? rawTitle : ''
  const description =
    photoDescription(photo) ||
    contentParagraphs[index] ||
    (index === 0 ? newsDescription?.trim() : '') ||
    ''

  return { title, description }
}

export default function PhotoGallery({
  photos,
  newsTitle,
  newsId,
  categorySlug,
  newsSlug,
  content,
  newsDescription,
}: PhotoGalleryProps) {
  const sortedPhotos = useMemo(
    () =>
      [...photos].sort((a, b) => {
        const sortA = a.imageSort ?? 0
        const sortB = b.imageSort ?? 0
        return sortA - sortB
      }),
    [photos],
  )

  const contentParagraphs = useMemo(() => extractContentParagraphs(content || ''), [content])

  const total = sortedPhotos.length
  const [visibleCount, setVisibleCount] = useState(() => Math.min(INITIAL_VISIBLE, sortedPhotos.length))
  const sentinelRef = useRef<HTMLDivElement>(null)
  const lightboxReadyRef = useRef(false)

  useEffect(() => {
    lightboxReadyRef.current = false
    const timer = window.setTimeout(() => {
      lightboxReadyRef.current = true
    }, 500)
    return () => window.clearTimeout(timer)
  }, [newsId])

  useEffect(() => {
    const fromHash = getVisibleCountFromHash(sortedPhotos.length)
    setVisibleCount(fromHash ?? Math.min(INITIAL_VISIBLE, sortedPhotos.length))
  }, [newsId, sortedPhotos.length])

  useEffect(() => {
    if (visibleCount >= total) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((prev) => Math.min(prev + LOAD_STEP, total))
        }
      },
      { rootMargin: '240px 0px', threshold: 0 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [visibleCount, total])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const match = window.location.hash.match(/^#photo(\d+)$/)
    if (!match) return

    const target = parseInt(match[1], 10)
    if (target > visibleCount) return

    const el = document.getElementById(`photo${target}`)
    if (!el) return

    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [visibleCount])

  const lightboxItems = useMemo(
    () =>
      sortedPhotos.flatMap((photo, index) => {
        const { title, description } = getPhotoCaption(
          photo,
          index,
          contentParagraphs,
          newsTitle,
          newsDescription,
        )
        const src = getImageUrl(photo.image) || getImageUrl(photo.image, IMAGE_PRESETS.gallery)
        return src
          ? [
              {
                src,
                alt: title || description || newsTitle,
                title: title || undefined,
                description: description || undefined,
                thumb: getImageUrl(photo.image, IMAGE_PRESETS.thumb) || src,
              },
            ]
          : []
      }),
    [contentParagraphs, newsDescription, newsTitle, sortedPhotos],
  )

  const openPhoto = (index: number, originEl: Element | null) => {
    if (!lightboxReadyRef.current) return
    openFancybox(lightboxItems, index, originEl)
  }

  if (sortedPhotos.length === 0) return null

  const articlePath = `/${categorySlug}/${newsSlug}-${newsId}`
  const visiblePhotos = sortedPhotos.slice(0, visibleCount)
  const hasMore = visibleCount < total

  return (
    <div className="mb-8 space-y-10">
      {visiblePhotos.map((photo, index) => {
        const shareUrl = `${absoluteUrl(articlePath)}#photo${index + 1}`
        const { title, description } = getPhotoCaption(
          photo,
          index,
          contentParagraphs,
          newsTitle,
          newsDescription,
        )

        return (
          <article key={photo.id} id={`photo${index + 1}`} className="w-full scroll-mt-28">
            <div className="relative mb-4 w-full overflow-hidden rounded-xl bg-neutral-200 shadow-md dark:bg-slate-800">
              <div
                className="relative aspect-[4/3] w-full cursor-zoom-in"
                role="button"
                tabIndex={0}
                aria-label={`${title || description || newsTitle} görselini büyüt`}
                onClick={(event) => {
                  openPhoto(index, event.currentTarget.querySelector('img') || event.currentTarget)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openPhoto(index, event.currentTarget.querySelector('img') || event.currentTarget)
                  }
                }}
              >
                {getImageUrl(photo.image, IMAGE_PRESETS.gallery) ? (
                  <Image
                    src={getImageUrl(photo.image, IMAGE_PRESETS.gallery)}
                    alt={title || description || newsTitle}
                    fill
                    className="object-cover"
                    priority={index === 0}
                    sizes="(max-width: 768px) 100vw, 896px"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                    <svg className="h-16 w-16 text-gray-400 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}

                <div className="photo-gallery-overlay">
                  <PhotoCounter current={index + 1} total={sortedPhotos.length} />
                  <PhotoShareBar
                    url={shareUrl}
                    title={newsTitle}
                    className="pointer-events-auto"
                  />
                </div>
              </div>
            </div>

            {title || description ? (
              <div className="photo-gallery-caption mx-auto max-w-3xl space-y-2.5">
                {title ? (
                  <h3 className="text-[1.45rem] font-bold leading-snug text-gray-900 dark:text-gray-100 sm:text-[1.75rem] md:text-[1.9rem]">
                    {title}
                  </h3>
                ) : null}
                {description ? (
                  <p className="text-[1.05rem] leading-relaxed text-gray-600 dark:text-gray-400 sm:text-[1.125rem]">
                    {description}
                  </p>
                ) : null}
              </div>
            ) : null}
          </article>
        )
      })}

      {hasMore && (
        <div ref={sentinelRef} className="flex flex-col items-center gap-3 py-2" aria-hidden={false}>
          <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
            <div
              className="h-full rounded-full bg-red-600 transition-all duration-300"
              style={{ width: `${(visibleCount / total) * 100}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {visibleCount} / {total} fotoğraf · kaydırmaya devam edin
          </p>
          <div className="h-24 w-full max-w-3xl animate-pulse rounded-xl bg-gray-100 dark:bg-slate-800" />
        </div>
      )}
    </div>
  )
}

function PhotoCounter({ current, total }: { current: number; total: number }) {
  return (
    <div className="photo-counter photo-counter--overlay" aria-label={`Fotoğraf ${current} / ${total}`}>
      <span className="photo-counter-current">{current}</span>
      <span className="photo-counter-sep" aria-hidden="true" />
      <span className="photo-counter-total">{total}</span>
    </div>
  )
}

function PhotoShareBar({
  url,
  title,
  className,
}: {
  url: string
  title: string
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1.5 sm:gap-2', className)} role="group" aria-label="Paylaş">
      <ShareLink
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        label="Facebook"
        platform="facebook"
      >
        <FacebookIcon />
      </ShareLink>
      <ShareLink
        href={`https://x.com/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}
        label="X"
        platform="x"
      >
        <XIcon />
      </ShareLink>
      <ShareLink
        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`}
        label="WhatsApp"
        platform="whatsapp"
      >
        <WhatsAppIcon />
      </ShareLink>
    </div>
  )
}

function ShareLink({
  href,
  label,
  platform,
  children,
}: {
  href: string
  label: string
  platform: 'facebook' | 'x' | 'whatsapp'
  children: ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`${label}'ta paylaş`}
      className={cn('photo-share-btn photo-share-btn--overlay', `photo-share-btn--${platform}`)}
    >
      <span className="size-[1.125rem] shrink-0 [&>svg]:size-full">{children}</span>
    </a>
  )
}

function FacebookIcon() {
  return (
    <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  )
}
